import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { getLeads, createLead, updateLead, deleteLead, bulkCreateLeads, getTemplates, getPipelineStatuses, getCallOutcomes, addLeadActivity } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash2, Phone, Mail, Building2, X, Users, Upload, CalendarClock, MapPin, MessageCircle, PhoneCall } from "lucide-react";
import { toast } from "sonner";
import { MatchingPackages } from "@/components/MatchingPackages";
import { CallHistory } from "@/components/CallHistory";
import { FormSubmissions } from "@/components/FormSubmissions";
import { AdminNotes } from "@/components/AdminNotes";
import { LeadAssign } from "@/components/LeadAssign";
import { BulkAssignBar } from "@/components/BulkAssignBar";
import { useNavigate } from "react-router-dom";

const todayStr = () => new Date().toISOString().slice(0, 10);
const isDue = (d) => d && d.slice(0, 10) <= todayStr();
const DONE_STATUS = ["appointment_scheduled", "assignment_received", "order_placed", "not_interested", "closed"];
const isDone = (l) => DONE_STATUS.includes(l.pipeline_status) || ["not_interested", "invalid_number"].includes(l.call_outcome);
const fmtDate = (d) => { if (!d) return ""; const [y, m, day] = d.slice(0, 10).split("-"); return `${day}-${m}-${y.slice(2)}`; };

const waNumber = (phone) => {
  let p = (phone || "").replace(/[^\d+]/g, "");
  if (p.startsWith("+")) p = p.slice(1);
  else if (p.startsWith("00")) p = p.slice(2);
  else if (p.startsWith("0")) p = "31" + p.slice(1);
  if (p.startsWith("310")) p = "31" + p.slice(3);
  return p;
};
const fillTemplate = (text, lead) =>
  (text || "").replaceAll("{{naam}}", lead.name || "").replaceAll("{{voornaam}}", (lead.name || "").split(" ")[0]);

// Convert markdown [label](url) and <a href="url">label</a> to plain clickable
// "label: url" so links survive into WhatsApp (auto-linked) and plain mailto bodies.
const normalizeLinks = (text) =>
  (text || "")
    .replace(/<a\s+[^>]*href=["']([^"']+)["'][^>]*>(.*?)<\/a>/gi, (_, url, label) => {
      const l = label.replace(/<[^>]+>/g, "").trim();
      return l && l !== url ? `${l}: ${url}` : url;
    })
    .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/gi, (_, label, url) =>
      label.trim() && label.trim() !== url ? `${label.trim()}: ${url}` : url)
    .replace(/<\/?[^>]+>/g, ""); // strip any leftover html tags

// Robust mailto open that works on desktop + mobile.
const openMail = (to, subject, body) => {
  const href = `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  const a = document.createElement("a");
  a.href = href;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => document.body.removeChild(a), 200);
};

const CATEGORIES = [
  { id: "verkoper", label: "Verkoper" },
  { id: "verhuurder", label: "Verhuurder" },
  { id: "koper", label: "Koper" },
  { id: "huurder", label: "Huurder" },
  { id: "partner", label: "Partner / Dienstverlener" },
  { id: "overig", label: "Overig" },
];
const catLabel = (c) => (CATEGORIES.find((x) => x.id === c) || CATEGORIES[5]).label;
// Map a category to a WhatsApp/email template audience bucket
const audienceForCategory = (c) => ({ verkoper: "owner", verhuurder: "owner", koper: "buyer", huurder: "renter", partner: "partner" }[c] || "all");

// Brand-aligned (navy/teal/neutral) pipeline status tints — no green.
const PIPELINE_TINT = {
  new: { bg: "#EDF1F5", color: "#5B6675" },
  contact_attempt: { bg: "#E3E9F5", color: "#001C51" },
  in_conversation: { bg: "#D6F3F3", color: "#0C6E6E" },
  callback: { bg: "#E3E9F5", color: "#001C51" },
  self_service_interest: { bg: "#D6F3F3", color: "#0C6E6E" },
  guidance_interest: { bg: "#D6F3F3", color: "#0C6E6E" },
  full_service_qualified: { bg: "#001C51", color: "#FFFFFF" },
  payment_link_sent: { bg: "#EAFBFB", color: "#0C6E6E" },
  order_placed: { bg: "#16BFBF", color: "#FFFFFF" },
  appointment_scheduled: { bg: "#E3E9F5", color: "#001C51" },
  assignment_received: { bg: "#16BFBF", color: "#FFFFFF" },
  not_interested: { bg: "#F3DEDD", color: "#B0413E" },
  closed: { bg: "#001C51", color: "#FFFFFF" },
};
const pipeTint = (key) => PIPELINE_TINT[key] || { bg: "#EDF1F5", color: "#5B6675" };

const emptyForm = { name: "", company: "", phone: "", email: "", category: "overig", pipeline_status: "new", call_outcome: "", notes: "", form_answers: "", location: "", budget: "", rental_price: "", timeline: "", source: "", campaign: "", follow_up_date: "" };

// ---- CSV / paste parsing ----
function splitRow(line, delim) {
  const out = []; let cur = "", inQ = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') { if (inQ && line[i + 1] === '"') { cur += '"'; i++; } else inQ = !inQ; }
    else if (c === delim && !inQ) { out.push(cur); cur = ""; }
    else cur += c;
  }
  out.push(cur);
  return out.map((s) => s.trim());
}

function parseLeadsText(text, category) {
  const lines = text.trim().split(/\r?\n/).filter((l) => l.trim());
  if (!lines.length) return [];
  const first = lines[0];
  const counts = { ",": (first.match(/,/g) || []).length, ";": (first.match(/;/g) || []).length, "\t": (first.match(/\t/g) || []).length };
  const delim = Object.keys(counts).reduce((a, b) => (counts[b] > counts[a] ? b : a), ",");
  const rows = lines.map((l) => splitRow(l, delim));
  const header = rows[0].map((h) => h.toLowerCase());
  const hasHeader = header.some((h) => /full[_ ]?name|name|naam|e-?mail|phone|telefoon|tel|mobiel|bedrijf|company/.test(h));
  const idx = {};
  if (hasHeader) {
    header.forEach((h, i) => {
      if (idx.name == null && /full[_ ]?name|name|naam/.test(h)) idx.name = i;
      if (idx.phone == null && /phone[_ ]?number|phone|telefoon|mobiel|^tel/.test(h)) idx.phone = i;
      if (idx.email == null && /e-?mail/.test(h)) idx.email = i;
      if (idx.company == null && /company|bedrijf/.test(h)) idx.company = i;
    });
  }
  if (idx.name == null) idx.name = 0;
  if (idx.phone == null) idx.phone = 1;
  if (idx.email == null) idx.email = 2;

  const mapped = new Set([idx.name, idx.phone, idx.email, idx.company].filter((v) => v != null));
  const SKIP = /^(id|created_time|created|updated_time|ad_id|ad_name|adset_id|adset_name|campaign_id|campaign_name|form_id|form_name|is_organic|organic|platform|lead_status|partner_name|retailer_item_id|conversion.*|utm[_-].*|tracking.*)$/;
  const pretty = (h) => h.replace(/_/g, " ").replace(/\s+/g, " ").trim().replace(/^./, (c) => c.toUpperCase());
  const campaignIdx = hasHeader ? header.findIndex((h) => /campaign_name/.test(h)) : -1;

  const dataRows = hasHeader ? rows.slice(1) : rows;
  return dataRows
    .map((r) => {
      let answers = [];
      if (hasHeader) {
        header.forEach((h, i) => {
          if (mapped.has(i) || SKIP.test(h)) return;
          const val = (r[i] || "").trim();
          if (val) answers.push(`${pretty(h)}: ${val}`);
        });
      }
      return {
        name: (r[idx.name] || "").trim(),
        phone: (r[idx.phone] || "").trim(),
        email: (r[idx.email] || "").trim(),
        company: (idx.company != null ? r[idx.company] || "" : "").trim(),
        category,
        source: "Meta advertentie",
        campaign: campaignIdx >= 0 ? (r[campaignIdx] || "").trim() : "",
        form_answers: answers.length ? answers.join("\n") : "",
      };
    })
    .filter((l) => l.name);
}

function SendModal({ ctx, templates, onClose }) {
  if (!ctx) return null;
  const { lead, channel } = ctx;
  const aud = audienceForCategory(lead.category);
  const list = templates.filter((t) => t.channel === channel && (t.audience === aud || t.audience === "all"));
  const send = (t) => {
    const body = normalizeLinks(fillTemplate(t.body, lead));
    if (channel === "whatsapp") {
      const num = waNumber(lead.phone);
      if (!num) { toast.error("Geen telefoonnummer bij deze lead"); return; }
      window.open(`https://wa.me/${num}?text=${encodeURIComponent(body)}`, "_blank");
      addLeadActivity(lead.id, "whatsapp", t.name || "").catch(() => {});
    } else {
      if (!lead.email) { toast.error("Geen e-mailadres bij deze lead"); return; }
      const subject = normalizeLinks(fillTemplate(t.subject || "", lead));
      openMail(lead.email, subject, body);
      addLeadActivity(lead.id, "email", subject || t.name || "").catch(() => {});
      toast.message("E-mailprogramma openen…", { description: "Opent je standaard e-mailapp niet? Stel er dan één in (zie uitleg in Beheer → Berichten)." });
    }
    onClose();
  };
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto fe-scroll fe-fade-up" onClick={(e) => e.stopPropagation()} data-testid="send-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E2E7EC] sticky top-0 bg-white">
          <h3 className="font-heading font-semibold text-xl text-[#16202B] flex items-center gap-2">
            {channel === "whatsapp" ? <MessageCircle className="h-5 w-5 text-[#16BFBF]" /> : <Mail className="h-5 w-5 text-[#001C51]" />}
            {channel === "whatsapp" ? "WhatsApp sturen" : "E-mail sturen"}
          </h3>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-[#EDF1F5]"><X className="h-5 w-5 text-[#5B6675]" /></button>
        </div>
        <div className="p-5">
          <p className="text-sm text-[#5B6675] mb-4">Naar <b className="text-[#16202B]">{lead.name}</b> — kies een bericht. De naam wordt automatisch ingevuld.</p>
          {list.length === 0 ? (
            <p className="text-center text-[#5B6675] py-8" data-testid="send-empty">Nog geen {channel === "whatsapp" ? "WhatsApp" : "e-mail"}-sjabloon voor deze categorie. Maak er een aan onder Beheer → Berichten.</p>
          ) : (
            <div className="space-y-3">
              {list.map((t) => (
                <button key={t.id} data-testid={`send-tpl-${t.id}`} onClick={() => send(t)}
                  className="w-full text-left rounded-xl border border-[#E2E7EC] p-4 hover:border-[#001C51] hover:bg-[#F6F9FB] transition-all duration-200">
                  <p className="font-heading font-semibold text-[#16202B] mb-1">{t.title}</p>
                  {t.subject ? <p className="text-xs text-[#5B6675] mb-1">Onderwerp: {t.subject}</p> : null}
                  <p className="text-sm text-[#5B6675] line-clamp-2 whitespace-pre-wrap">{fillTemplate(t.body, lead).slice(0, 140)}…</p>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

function ImportModal({ open, onClose, onImported }) {
  const [text, setText] = useState("");
  const [category, setCategory] = useState("verkoper");
  const [importing, setImporting] = useState(false);
  const preview = open ? parseLeadsText(text, category) : [];

  const doImport = async () => {
    if (!preview.length) return toast.error("Geen leads herkend. Controleer je gegevens.");
    setImporting(true);
    try {
      const res = await bulkCreateLeads(preview);
      toast.success(`${res.inserted} leads geïmporteerd${res.skipped ? ` · ${res.skipped} dubbel overgeslagen` : ""}`);
      setText("");
      onImported();
      onClose();
    } catch { toast.error("Importeren mislukt"); }
    finally { setImporting(false); }
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setText(String(reader.result || ""));
    reader.readAsText(file);
  };

  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto fe-scroll fe-fade-up" onClick={(e) => e.stopPropagation()} data-testid="import-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E2E7EC] sticky top-0 bg-white">
          <h3 className="font-heading font-semibold text-xl text-[#16202B]">Leads importeren</h3>
          <button onClick={onClose} data-testid="import-close" className="p-2 rounded-lg hover:bg-[#EDF1F5]"><X className="h-5 w-5 text-[#5B6675]" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="rounded-xl bg-[#F6F9FB] border border-[#E2E7EC] p-4 text-sm text-[#5B6675] leading-relaxed">
            Plak je Meta-leads hieronder (of upload het CSV-bestand). Dubbele contacten (zelfde telefoon/e-mail) worden automatisch overgeslagen. De originele formulierantwoorden worden apart bewaard.
          </div>
          <div className="flex items-center gap-3">
            <label className="block flex-1">
              <span className="text-sm text-[#5B6675] mb-1.5 block">Categorie voor deze batch</span>
              <select data-testid="import-type" value={category} onChange={(e) => setCategory(e.target.value)} className="fe-input">
                {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </label>
            <label data-testid="import-file-label" className="mt-6 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#E2E7EC] text-[#5B6675] font-medium hover:bg-[#EDF1F5] cursor-pointer">
              <Upload className="h-4 w-4" /> CSV uploaden
              <input data-testid="import-file" type="file" accept=".csv,text/csv,text/plain" onChange={onFile} className="hidden" />
            </label>
          </div>
          <label className="block">
            <span className="text-sm text-[#5B6675] mb-1.5 block">Of plak hier je leads</span>
            <textarea data-testid="import-textarea" value={text} onChange={(e) => setText(e.target.value)} rows={7}
              placeholder={"full_name,phone_number,email\nJan de Vries,0612345678,jan@email.nl"}
              className="fe-input resize-y font-mono text-[13px]" />
          </label>
          {text.trim() && (
            <div className="rounded-xl border border-[#E2E7EC] overflow-hidden" data-testid="import-preview">
              <div className="px-4 py-2 bg-[#EDF1F5] text-sm font-semibold text-[#001C51]">{preview.length} leads herkend</div>
              <div className="max-h-48 overflow-y-auto fe-scroll">
                {preview.slice(0, 25).map((l, i) => (
                  <div key={i} className="px-4 py-2 border-t border-[#E2E7EC] text-sm flex gap-4 items-center">
                    <span className="font-medium text-[#16202B] min-w-[140px]">{l.name}</span>
                    <span className="text-[#5B6675]">{l.phone}</span>
                    <span className="text-[#5B6675]">{l.email}</span>
                    {l.form_answers && <span className="ml-auto text-xs font-medium text-[#0C6E6E] bg-[#D6F3F3] rounded-full px-2 py-0.5 whitespace-nowrap">+ antwoorden</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="px-6 py-4 border-t border-[#E2E7EC] flex justify-end gap-3 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-5 py-2.5 rounded-xl border border-[#E2E7EC] text-[#5B6675] font-medium hover:bg-[#EDF1F5]">Annuleren</button>
          <button data-testid="confirm-import" onClick={doImport} disabled={importing || !preview.length} className="px-5 py-2.5 rounded-xl bg-[#001C51] text-white font-medium hover:bg-[#0A337A] disabled:opacity-50">
            {importing ? "Importeren…" : `Importeer ${preview.length || ""} leads`}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-sm text-[#5B6675] mb-1.5 block">{label}</span>
    {children}
  </label>
);

function LeadModal({ open, onClose, onSaved, editing, pipeline, outcomes }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  useEffect(() => { setForm(editing ? { ...emptyForm, ...editing } : emptyForm); }, [editing, open]);
  if (!open) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setFollow = (days) => { const d = new Date(); d.setDate(d.getDate() + days); setForm((f) => ({ ...f, follow_up_date: d.toISOString().slice(0, 10) })); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Naam is verplicht");
    setSaving(true);
    try {
      const payload = {
        name: form.name, company: form.company, phone: form.phone, email: form.email,
        category: form.category, pipeline_status: form.pipeline_status, call_outcome: form.call_outcome || null,
        notes: form.notes, form_answers: form.form_answers, location: form.location || "",
        budget: form.budget || "", rental_price: form.rental_price || "", timeline: form.timeline || "",
        source: form.source || "", campaign: form.campaign || "", follow_up_date: form.follow_up_date || "",
      };
      if (editing?.id) await updateLead(editing.id, payload);
      else await createLead(payload);
      toast.success(editing ? "Lead bijgewerkt" : "Lead toegevoegd");
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Opslaan mislukt"); }
    finally { setSaving(false); }
  };

  const activity = editing?.activity || [];

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg lg:max-w-6xl max-h-[92vh] overflow-y-auto fe-scroll fe-fade-up" onClick={(e) => e.stopPropagation()} data-testid="lead-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E2E7EC] sticky top-0 bg-white">
          <h3 className="font-heading font-semibold text-xl text-[#16202B]">{editing ? "Lead bewerken" : "Nieuwe lead"}</h3>
          <button onClick={onClose} data-testid="modal-close" className="p-2 rounded-lg hover:bg-[#EDF1F5]"><X className="h-5 w-5 text-[#5B6675]" /></button>
        </div>
        <div className="p-6 grid gap-6 lg:grid-cols-2 items-start">
        <div className="space-y-4">
          <Field label="Naam *"><input data-testid="input-name" value={form.name} onChange={set("name")} className="fe-input" placeholder="Jan de Vries" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Categorie">
              <select data-testid="input-type" value={form.category} onChange={set("category")} className="fe-input">
                {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </Field>
            <Field label="Pipeline status">
              <select data-testid="input-status" value={form.pipeline_status} onChange={set("pipeline_status")} className="fe-input">
                {pipeline.filter((s) => s.active).map((s) => <option key={s.id} value={s.key}>{s.label}</option>)}
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Laatste gespreksuitkomst">
              <select data-testid="input-outcome" value={form.call_outcome || ""} onChange={set("call_outcome")} className="fe-input">
                <option value="">—</option>
                {outcomes.filter((o) => o.active).map((o) => <option key={o.id} value={o.key}>{o.label}</option>)}
              </select>
            </Field>
            <Field label="Bron / campagne"><input data-testid="input-source" value={form.source} onChange={set("source")} className="fe-input" placeholder="Bijv. Meta advertentie" /></Field>
          </div>
          <Field label="Bedrijf"><input data-testid="input-company" value={form.company} onChange={set("company")} className="fe-input" placeholder="Optioneel" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Telefoon"><input data-testid="input-phone" value={form.phone} onChange={set("phone")} className="fe-input" placeholder="06 12345678" /></Field>
            <Field label="E-mail"><input data-testid="input-email" value={form.email} onChange={set("email")} className="fe-input" placeholder="naam@email.nl" /></Field>
          </div>

          <div className="pt-2 border-t border-[#E2E7EC]">
            <p className="text-xs uppercase tracking-wide text-[#16BFBF] font-semibold mb-3">Over de woning & wens</p>
            <div className="space-y-4">
              <Field label="Locatie van de woning (adres / plaats)"><input data-testid="input-location" value={form.location} onChange={set("location")} className="fe-input" placeholder="Bijv. Prinsengracht 12, Amsterdam" /></Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Vraagprijs / waarde"><input data-testid="input-budget" value={form.budget} onChange={set("budget")} className="fe-input" placeholder="Bijv. € 450.000" /></Field>
                <Field label="Huurprijs (indien verhuur)"><input data-testid="input-rental" value={form.rental_price} onChange={set("rental_price")} className="fe-input" placeholder="Bijv. € 1.500/mnd" /></Field>
              </div>
              <Field label="Wanneer? (tijdlijn)"><input data-testid="input-timeline" value={form.timeline} onChange={set("timeline")} className="fe-input" placeholder="Bijv. binnen 3 maanden" /></Field>
            </div>
          </div>

          <Field label="Opvolgen op (terugbellen)">
            <input type="date" data-testid="input-followup" value={(form.follow_up_date || "").slice(0, 10)} onChange={set("follow_up_date")} className="fe-input" />
            <div className="flex flex-wrap gap-2 mt-2">
              {[{ d: 1, l: "Morgen" }, { d: 3, l: "Over 3 dagen" }, { d: 7, l: "Volgende week" }].map((q) => (
                <button key={q.d} type="button" onClick={() => setFollow(q.d)} className="text-xs font-medium px-3 py-1.5 rounded-full border border-[#E2E7EC] text-[#5B6675] hover:bg-[#EDF1F5] transition-colors">{q.l}</button>
              ))}
              <button type="button" onClick={() => setForm((f) => ({ ...f, follow_up_date: "" }))} className="text-xs font-medium px-3 py-1.5 rounded-full border border-[#E2E7EC] text-[#B0413E] hover:bg-[#F3DEDD] transition-colors">Wissen</button>
            </div>
          </Field>
          {editing?.id && <CallHistory leadId={editing.id} />}
        </div>

        <div className="space-y-4">
          <Field label="Interne notities (wat is besproken…)"><textarea data-testid="input-notes" value={form.notes} onChange={set("notes")} rows={6} className="fe-input resize-y" placeholder="Schrijf hier alles op: motivatie, bezwaren, afspraken, persoonlijke details…" /></Field>
          {(editing?.asking_price || editing?.postcode) && (
            <div className="rounded-xl border border-[#E2E7EC] bg-white p-3 text-sm grid grid-cols-2 gap-2" data-testid="lead-property">
              {editing.address && <p><span className="text-[#5B6675]">Adres: </span>{editing.address} {editing.house_number}</p>}
              {editing.postcode && <p><span className="text-[#5B6675]">Postcode: </span>{editing.postcode}</p>}
              {editing.asking_price && <p><span className="text-[#5B6675]">Vraagprijs/waarde: </span>{editing.asking_price}</p>}
              {editing.consent && <p><span className="text-[#5B6675]">Toestemming: </span>{editing.consent}</p>}
            </div>
          )}
          {editing?.form_submissions?.length ? <FormSubmissions lead={editing} /> : form.form_answers ? (
            <div className="rounded-xl border border-[#E2E7EC] bg-[#F6F9FB] p-4" data-testid="form-answers">
              <p className="text-xs uppercase tracking-wide text-[#5B6675] font-semibold mb-2">Formulierantwoorden</p>
              <pre className="text-sm text-[#16202B] whitespace-pre-wrap font-sans leading-relaxed">{form.form_answers}</pre>
            </div>
          ) : null}
          {editing?.id && (form.category === "verkoper" || form.category === "verhuurder") && (
            <MatchingPackages lead={{ ...editing, category: form.category }} />
          )}

          {activity.length > 0 && (
            <div className="pt-2 border-t border-[#E2E7EC]" data-testid="activity-log">
              <p className="text-xs uppercase tracking-wide text-[#16BFBF] font-semibold mb-2">Activiteit ({activity.length})</p>
              <div className="space-y-1.5 max-h-40 overflow-y-auto fe-scroll">
                {[...activity].reverse().map((a, i) => (
                  <div key={i} className="text-xs text-[#5B6675] flex gap-2">
                    <span className="text-[#8994A3] whitespace-nowrap">{fmtDate(a.at)}</span>
                    <span className="text-[#16202B]">{a.detail}</span>
                    <span className="ml-auto text-[#8994A3]">{a.by}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        </div>
        <div className="px-6 py-4 border-t border-[#E2E7EC] flex justify-end gap-3 sticky bottom-0 bg-white">
          <button onClick={onClose} className="px-5 py-2.5 rounded-xl border border-[#E2E7EC] text-[#5B6675] font-medium hover:bg-[#EDF1F5]">Annuleren</button>
          <button data-testid="save-lead" onClick={save} disabled={saving} className="px-5 py-2.5 rounded-xl bg-[#001C51] text-white font-medium hover:bg-[#0A337A] disabled:opacity-50">{saving ? "Opslaan…" : "Opslaan"}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function CategoryPicker({ lead, onChange }) {
  return (
    <select data-testid={`type-select-${lead.id}`} value={lead.category || "overig"} onChange={(e) => onChange(lead.id, e.target.value)} onClick={(e) => e.stopPropagation()} title="Categorie"
      className="text-xs font-medium rounded-full px-3 py-1.5 border border-[#E2E7EC] bg-white text-[#5B6675] outline-none cursor-pointer hover:border-[#001C51] transition-colors">
      {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
    </select>
  );
}

function PipelinePicker({ lead, pipeline, onChange }) {
  const m = pipeTint(lead.pipeline_status);
  return (
    <select data-testid={`status-select-${lead.id}`} value={lead.pipeline_status || "new"} onChange={(e) => onChange(lead.id, e.target.value)} onClick={(e) => e.stopPropagation()}
      className="text-xs font-semibold rounded-full px-3 py-1.5 border-0 outline-none cursor-pointer" style={{ background: m.bg, color: m.color }}>
      {pipeline.filter((s) => s.active).map((s) => <option key={s.id} value={s.key}>{s.label}</option>)}
    </select>
  );
}

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [sendCtx, setSendCtx] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [pipeline, setPipeline] = useState([]);
  const [outcomes, setOutcomes] = useState([]);
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canAssign = isAdmin || user?.role === "teamleider" || (user?.permissions || []).includes("assign_leads");
  const [selected, setSelected] = useState([]);
  const toggleSel = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const load = () => getLeads().then(setLeads).catch(() => {});
  useEffect(() => {
    load();
    getTemplates().then(setTemplates).catch(() => {});
    getPipelineStatuses().then(setPipeline).catch(() => {});
    getCallOutcomes().then(setOutcomes).catch(() => {});
  }, []);

  const changePipeline = async (id, pipeline_status) => {
    await updateLead(id, { pipeline_status });
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, pipeline_status } : l)));
    toast.success("Status bijgewerkt");
  };
  const changeCategory = async (id, category) => {
    await updateLead(id, { category });
    setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, category } : l)));
    toast.success("Categorie bijgewerkt");
  };
  const remove = async (id) => {
    try { await deleteLead(id); setLeads((ls) => ls.filter((l) => l.id !== id)); toast.success("Lead verwijderd"); }
    catch (e) { toast.error(e?.response?.status === 403 ? "Geen rechten om te verwijderen" : "Verwijderen mislukt"); }
  };

  const [dueOnly, setDueOnly] = useState(false);
  const isDueCall = (l) => isDue(l.follow_up_date) && !isDone(l);
  const dueCount = leads.filter(isDueCall).length;
  const byCat = filter === "all" ? leads : leads.filter((l) => l.category === filter);
  const shown = dueOnly ? byCat.filter(isDueCall).sort((a, b) => a.follow_up_date.localeCompare(b.follow_up_date)) : byCat;
  const FILTERS = [{ id: "all", label: "Alle" }, ...CATEGORIES];

  return (
    <div className="px-6 lg:px-12 py-10 max-w-6xl mx-auto fe-fade-up">
      <header className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight text-[#16202B]">Leads</h1>
          <p className="text-[#5B6675] mt-2">Klik op een lead voor details, pipeline, gespreksuitkomst en activiteit.</p>
        </div>
        <div className="flex items-center gap-3">
          <button data-testid="import-leads" onClick={() => setImportOpen(true)} className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-[#E2E7EC] bg-white text-[#001C51] font-medium hover:bg-[#EDF1F5] hover:-translate-y-0.5 transition-all duration-200">
            <Upload className="h-4 w-4" /> Importeer
          </button>
          <button data-testid="add-lead" onClick={() => { setEditing(null); setModalOpen(true); }} className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#001C51] text-white font-medium hover:bg-[#0A337A] hover:-translate-y-0.5 transition-all duration-200">
            <Plus className="h-4 w-4" /> Nieuwe lead
          </button>
        </div>
      </header>

      <div className="inline-flex flex-wrap gap-1 rounded-xl border border-[#E2E7EC] bg-white p-1 mb-6">
        {FILTERS.map((f) => (
          <button key={f.id} data-testid={`filter-${f.id}`} onClick={() => setFilter(f.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${filter === f.id ? "bg-[#001C51] text-white" : "text-[#5B6675] hover:bg-[#EDF1F5]"}`}>
            {f.label}
          </button>
        ))}
      </div>
      <button data-testid="filter-due" onClick={() => setDueOnly((v) => !v)}
        className={`ml-2 mb-6 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm font-medium transition-colors ${dueOnly ? "bg-[#B0413E] text-white border-[#B0413E]" : "bg-white text-[#B0413E] border-[#F3DEDD] hover:bg-[#FDF6F6]"}`}>
        <CalendarClock className="h-4 w-4" /> Nog te bellen ({dueCount})
      </button>

      {shown.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#E2E7EC] bg-white p-16 text-center" data-testid="leads-empty">
          <div className="h-14 w-14 rounded-2xl bg-[#EDF1F5] flex items-center justify-center mx-auto mb-4">
            <Users className="h-6 w-6 text-[#001C51]" strokeWidth={1.5} />
          </div>
          <p className="font-heading font-semibold text-lg text-[#16202B] mb-1">Nog geen leads</p>
          <p className="text-[#5B6675] mb-5">Voeg je eerste contact toe en begin met bellen.</p>
          <button onClick={() => { setEditing(null); setModalOpen(true); }} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#001C51] text-white font-medium hover:bg-[#0A337A]">
            <Plus className="h-4 w-4" /> Lead toevoegen
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {shown.map((lead) => (
            <div key={lead.id} data-testid={`lead-row-${lead.id}`} onClick={() => { setEditing(lead); setModalOpen(true); }}
              className={`rounded-2xl border bg-white p-5 flex flex-wrap ${selected.includes(lead.id) ? "border-[#16BFBF] ring-1 ring-[#16BFBF]" : "border-[#E2E7EC]"} items-center gap-4 cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md`}>
              {canAssign && (
                <input type="checkbox" data-testid={`select-lead-${lead.id}`} checked={selected.includes(lead.id)}
                  onClick={(e) => e.stopPropagation()} onChange={() => toggleSel(lead.id)}
                  className="h-4 w-4 accent-[#16BFBF] cursor-pointer shrink-0" />
              )}
              <div className="h-11 w-11 rounded-xl bg-[#EDF1F5] flex items-center justify-center shrink-0 font-heading font-bold text-[#001C51]">
                {lead.name.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-[160px]">
                <p className="font-heading font-semibold text-[#16202B]">{lead.name}</p>
                <p className="text-sm text-[#5B6675] flex items-center gap-1">
                  {lead.category === "partner" ? <Building2 className="h-3.5 w-3.5" /> : null}
                  {lead.company || catLabel(lead.category)}
                </p>
                {lead.location && <p className="text-xs text-[#5B6675] flex items-center gap-1 mt-0.5"><MapPin className="h-3 w-3" /> {lead.location}</p>}
              </div>
              <div className="text-sm text-[#5B6675] space-y-0.5 min-w-[150px]">
                {lead.phone && <p className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> {lead.phone}</p>}
                {lead.email && <p className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> {lead.email}</p>}
              </div>
              {lead.call_attempts > 0 && (
                <div title="Belpogingen" className="text-xs font-medium px-2.5 py-1 rounded-full flex items-center gap-1 bg-[#EDF1F5] text-[#5B6675]">
                  <PhoneCall className="h-3.5 w-3.5" /> {lead.call_attempts}×
                </div>
              )}
              {lead.follow_up_date && !isDone(lead) && (
                <div data-testid={`followup-${lead.id}`} title="Opvolgdatum"
                  className={`text-xs font-medium px-2.5 py-1 rounded-full flex items-center gap-1 ${isDue(lead.follow_up_date) ? "bg-[#F3DEDD] text-[#B0413E]" : "bg-[#E3E9F5] text-[#001C51]"}`}>
                  <CalendarClock className="h-3.5 w-3.5" /> {isDue(lead.follow_up_date) ? "Bellen" : fmtDate(lead.follow_up_date)}
                </div>
              )}
              <LeadAssign lead={lead} isAdmin={canAssign} onChanged={(u) => setLeads((ls) => ls.map((l) => (l.id === u.id ? u : l)))} />
              <div className="ml-auto flex items-center gap-2">
                <CategoryPicker lead={lead} onChange={changeCategory} />
                <PipelinePicker lead={lead} pipeline={pipeline} onChange={changePipeline} />
                <button data-testid={`call-${lead.id}`} onClick={(e) => { e.stopPropagation(); navigate(`/belomgeving/${lead.id}`); }} title="Open in belomgeving"
                  className="p-2 rounded-lg hover:bg-[#D6F3F3] text-[#16BFBF] transition-colors"><PhoneCall className="h-4 w-4" /></button>
                <button data-testid={`wa-${lead.id}`} onClick={(e) => { e.stopPropagation(); setSendCtx({ lead, channel: "whatsapp" }); }} title="WhatsApp sturen"
                  className="p-2 rounded-lg hover:bg-[#D6F3F3] text-[#0C6E6E] transition-colors"><MessageCircle className="h-4 w-4" /></button>
                <button data-testid={`email-${lead.id}`} onClick={(e) => { e.stopPropagation(); setSendCtx({ lead, channel: "email" }); }} title="E-mail sturen"
                  className="p-2 rounded-lg hover:bg-[#E3E9F5] text-[#001C51] transition-colors"><Mail className="h-4 w-4" /></button>
                {isAdmin && <button data-testid={`delete-${lead.id}`} onClick={(e) => { e.stopPropagation(); remove(lead.id); }} title="Verwijderen"
                  className="p-2 rounded-lg hover:bg-[#F3DEDD] text-[#B0413E] transition-colors"><Trash2 className="h-4 w-4" /></button>}
              </div>
              <AdminNotes lead={lead} isAdmin={isAdmin} onChanged={(u) => setLeads((ls) => ls.map((l) => (l.id === u.id ? u : l)))} />
            </div>
          ))}
        </div>
      )}

      {canAssign && <BulkAssignBar selected={selected} onClear={() => setSelected([])} onDone={() => { setSelected([]); load(); }} />}
      <LeadModal open={modalOpen} onClose={() => setModalOpen(false)} onSaved={load} editing={editing} pipeline={pipeline} outcomes={outcomes} />
      <ImportModal open={importOpen} onClose={() => setImportOpen(false)} onImported={load} />
      <SendModal ctx={sendCtx} templates={templates} onClose={() => setSendCtx(null)} />
    </div>
  );
}
